import React, { useContext } from 'react'
import { observer } from 'mobx-react'
import { StoresContext } from '@App/contexts'
import { formatName } from '@App/helpers/format-name'
import { WorkModel } from '@Entities/work/work'
import { ICONS } from '@Const/common'
import WorkMergeReason from '@Scenes/RightsholderScene/WorkMerge/WorkMergeReason'
import WorkMergeCandidate from '@Scenes/RightsholderScene/WorkMerge/WorkMergeSearch/components/WorkMergePanel/WorkMergePrimarySelection/WorkMergeCandidate'
import './index.scss'

interface WorkMergeRequestGroupProps {
    heading: string
    primaryWork: WorkModel
    secondaryWorks: WorkModel[]
    reason?: string
    onRemove?(): void
}

const getWriterNames = (writers: WorkModel['writers']) =>
    (writers ?? [])
        .map((writer) => formatName(writer.firstName, writer.lastName, writer.fullName))
        .filter(Boolean)
        .join(', ')

const getCollectionShare = (publisherShare: number | null) => (publisherShare == null ? 'N/A' : `${publisherShare}%`)

const WorkMergeRequestGroup = ({
    heading,
    primaryWork,
    secondaryWorks,
    reason,
    onRemove,
}: WorkMergeRequestGroupProps) => {
    const { rightsholderStore } = useContext(StoresContext)

    const publishers = secondaryWorks.flatMap((work) =>
        (work.originalPublishers ?? []).map((publisher, index) => ({
            key: `${work.id}-${publisher.id ?? index}`,
            publisher,
        }))
    )

    return (
        <div className="work-merge-request-group">
            <div className="work-merge-request-group-heading">
                <p className="work-merge-request-group-title">{heading}</p>
                {onRemove && (
                    <button className="work-merge-request-group-remove" type="button" onClick={onRemove}>
                        <img src={ICONS.trashCan} alt="" />
                        Remove request
                    </button>
                )}
            </div>

            <p className="work-merge-request-group-label">Primary Work</p>
            <WorkMergeCandidate
                work={primaryWork}
                yourShares={rightsholderStore.findSpecificWorkClaimedShares(primaryWork.id)}
            />

            {secondaryWorks.length > 0 && (
                <>
                    <p className="work-merge-request-group-label">
                        Works and Publishers being merged into the primary above
                    </p>
                    <table className="work-merge-request-group-table">
                        <thead>
                            <tr>
                                <th>Work Title</th>
                                <th>Writers</th>
                                <th>MLC Song Code</th>
                            </tr>
                        </thead>
                        <tbody>
                            {secondaryWorks.map((work) => (
                                <tr key={work.id}>
                                    <td>{work.title}</td>
                                    <td className="work-merge-request-group-table-truncated">
                                        {getWriterNames(work.writers)}
                                    </td>
                                    <td>{work.songCode}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {publishers.length > 0 && (
                <table className="work-merge-request-group-table">
                    <thead>
                        <tr>
                            <th>Publisher</th>
                            <th>Represented Writers</th>
                            <th>Collection Share</th>
                        </tr>
                    </thead>
                    <tbody>
                        {publishers.map(({ key, publisher }) => (
                            <tr key={key}>
                                <td>{publisher.publisherName}</td>
                                <td className="work-merge-request-group-table-truncated">
                                    {getWriterNames(publisher.writers)}
                                </td>
                                <td>{getCollectionShare(publisher.publisherShare)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <WorkMergeReason reason={reason} />
        </div>
    )
}

export default observer(WorkMergeRequestGroup)
